import {useCallback, useMemo, useSyncExternalStore} from "react";

/** Pins kept for destinations the bar can't show right now (a permission still loading, or taken away), at most. */
const STALE_KEPT = 4;
const CHANGED = "ots:nav-pins";

const storageKey = (key: string) => `ots.nav-pins.${key}`;

const readRaw = (key: string | undefined): string => {
  if (!key) return "[]";
  try {
    return localStorage.getItem(storageKey(key)) ?? "[]";
  } catch {
    return "[]";
  }
};

const parse = (raw: string): string[] => {
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const write = (key: string, ids: string[]) => {
  try {
    localStorage.setItem(storageKey(key), JSON.stringify(ids));
  } catch {
    // Private browsing or storage full: the pins last until the page reloads.
  }
  window.dispatchEvent(new Event(CHANGED));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGED, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener("storage", onChange);
  };
};

/**
 * Keeps the stored list in bounds when a pin is added: every pin that can show, plus the newest few that can't.
 * Stale pins are kept rather than dropped at once, because a destination can be missing only for a moment (its
 * permission is still loading) and the person shouldn't lose the pin for that.
 */
export const trimNavPins = (ids: string[], available: ReadonlySet<string>): string[] => {
  let staleToDrop = ids.filter(id => !available.has(id)).length - STALE_KEPT;
  return ids.filter(id => available.has(id) || staleToDrop-- <= 0);
};

export type OtsNavPins = {
  /** The pins that can show, in the order they were pinned, never more than the maximum. */
  pinned: string[];
  isPinned: (id: string) => boolean;
  /** Whether there is room for another pin. */
  canPin: boolean;
  max: number;
  toggle: (id: string) => void;
  unpin: (id: string) => void;
};

/**
 * Destinations the person has pinned to the status strip, remembered in this browser per app (`key`), and kept in
 * step across its tabs. Only ids in `available` show and count toward `max`; the rest are stale (gone, or hidden by
 * a permission) and wait, a few at most, in case they come back. Without a `key` nothing can be pinned.
 */
export const useOtsNavPins = (key: string | undefined, available: readonly string[], max = 4): OtsNavPins => {
  const raw = useSyncExternalStore(subscribe, () => readRaw(key), () => "[]");
  const stored = useMemo(() => parse(raw), [raw]);
  const availableSet = useMemo(() => new Set(available), [available]);
  const pinned = useMemo(() => stored.filter(id => availableSet.has(id)).slice(0, max), [stored, availableSet, max]);

  const unpin = useCallback((id: string) => {
    if (key) write(key, parse(readRaw(key)).filter(pin => pin !== id));
  }, [key]);

  const toggle = useCallback((id: string) => {
    if (!key) return;
    const current = parse(readRaw(key));
    if (current.includes(id)) {
      write(key, current.filter(pin => pin !== id));
      return;
    }
    if (current.filter(pin => availableSet.has(pin)).length >= max) return;
    write(key, trimNavPins([...current, id], availableSet));
  }, [key, availableSet, max]);

  return {
    pinned,
    isPinned: (id: string) => pinned.includes(id),
    canPin: !!key && pinned.length < max,
    max,
    toggle,
    unpin,
  };
};
